import { useQuery } from '@tanstack/react-query'
import { createApiCall } from 'services'
import type { Profile } from 'types'

// Dev bypass — when VITE_SKIP_AUTH=true, return a mock profile without hitting the API
const SKIP_AUTH = import.meta.env.VITE_SKIP_AUTH === 'true'
const DEV_BYPASS_PROFILE: Profile = {
  id: 'dev-bypass-user',
  first_name: 'Dev',
  last_name: 'Bypass',
  phone: '',
  email: 'dev-bypass@vera.local',
  avatar: null,
  title: null,
  department: null,
  theme: 'light',
  notification_settings: {
    system: { platform: true, email: true },
    account: { platform: true, email: true },
    billing: { platform: true, email: true },
    security: { platform: true, email: true },
    content: { platform: true, email: true },
    promotional: { platform: false, email: false },
    features: { platform: true, email: true },
  },
  active: true,
  role_id: 'dev-role',
  role: {
    id: 'dev-role',
    name: 'company_admin',
    level: 3,
    created_at: new Date().toISOString(),
    updated_at: null,
    permissions: [],
  },
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
}

const USER_PROFILE_QUERY_KEY = ['user', 'profile']

interface UserProfileResponse {
  id?: string
  email: string
  first_name?: string | null
  last_name?: string | null
  phone?: string | null
  avatar?: string | null
  title?: string | null
  department?: string | null
  theme?: 'light' | 'dark' | 'system' | null
  notification_settings?: Record<string, { platform: boolean; email: boolean }> | null
  active?: boolean
  role_id: string
  role?: {
    id?: string | null
    name?: string | null
    permissions?: string[]
  }
  created_at?: Date | string
  updated_at?: Date | string | null
}

type UseUserProfileResult = {
  data: Profile | null
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

// Map API response to Profile type
function mapApiResponseToProfile(apiResponse: UserProfileResponse): Profile {
  const firstName = apiResponse.first_name || ''
  const lastName = apiResponse.last_name || ''
  
  const rawRoleName = (apiResponse.role?.name || '').toLowerCase()
  const roleMap: Record<string, 'platform_admin' | 'platform_manager' | 'company_admin' | 'company_team_member'> = {
    platform_admin: 'platform_admin',
    platform_manager: 'platform_manager',
    company_admin: 'company_admin',
    team_member: 'company_team_member',
  }

  const roleType = roleMap[rawRoleName] || 'company_team_member'
  const roleLevel = roleType === 'company_admin' ? 3 : roleType === 'platform_admin' ? 1 : roleType === 'platform_manager' ? 2 : 4

  const permissions: string[] = Array.isArray(apiResponse.role?.permissions) ? apiResponse.role.permissions : []
  const nowIso = new Date().toISOString()
  const createdAt = apiResponse.created_at ? (typeof apiResponse.created_at === 'string' ? apiResponse.created_at : apiResponse.created_at.toISOString()) : nowIso
  const updatedAt = apiResponse.updated_at ? (typeof apiResponse.updated_at === 'string' ? apiResponse.updated_at : apiResponse.updated_at?.toISOString() || null) : nowIso

  return {
    id: apiResponse.id || '',
    first_name: firstName,
    last_name: lastName,
    phone: apiResponse.phone || '',
    email: apiResponse.email,
    avatar: apiResponse.avatar ?? null,
    title: apiResponse.title ?? null,
    department: apiResponse.department ?? null,
    theme: apiResponse.theme ?? 'light',
    notification_settings: apiResponse.notification_settings ?? undefined,
    active: apiResponse.active ?? true,
    role_id: apiResponse.role_id || apiResponse.role?.id || '',
    role: {
      id: apiResponse.role?.id || apiResponse.role_id || '',
      name: roleType,
      level: roleLevel,
      created_at: createdAt,
      updated_at: null,
      permissions: permissions.map((perm) => ({
        id: perm,
        name: perm as any,
        description: `${perm} permission`,
        created_at: nowIso,
        updated_at: null,
      })),
    },
    created_at: createdAt,
    updated_at: updatedAt,
  }
}

export const useUserProfileQuery = (options?: { enabled?: boolean }): UseUserProfileResult => {
  const isEnabled = options?.enabled !== false

  const query = useQuery({
    queryKey: USER_PROFILE_QUERY_KEY,
    queryFn: async () => {
      if (SKIP_AUTH) return null // data injected via initialData below
      const response = await createApiCall({
        method: 'GET',
        url: '/auth/admin/profile',
      })
      return response.data as UserProfileResponse
    },
    enabled: SKIP_AUTH ? false : isEnabled, // bypass: never fetch, use initialData
    initialData: SKIP_AUTH ? (null as unknown as UserProfileResponse) : undefined,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
    gcTime: 1000 * 60 * 10, // 10 minutes
  })

  return {
    data: SKIP_AUTH ? DEV_BYPASS_PROFILE : (query.data ? mapApiResponseToProfile(query.data) : null),
    isLoading: SKIP_AUTH ? false : query.isLoading,
    isError: SKIP_AUTH ? false : query.isError,
    error: SKIP_AUTH ? null : query.error,
    refetch: query.refetch,
  }
}

export { USER_PROFILE_QUERY_KEY }
